import { ParticipantAllHistoryTable } from './models/participant/participant-all-history-table.interface';
// tslint:disable:max-line-length */
import { AuthGuard } from './guards/auth.guard';
import { HybridAuthGuard } from './guards/hybrid-auth.guard';
import { LoginGuard } from './guards/login.guard';
import { PermissionRouteGuard } from './guards/permission-route.guard';
import { ProjectXGuard } from './guards/project-x.guard';

import { ApiEndpointInterceptor } from './interceptors/api-endpoint.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { HttpCredentialsInterceptor } from './interceptors/http-credentials.interceptor';
import { HttpHeadersInterceptor } from './interceptors/http-headers.interceptor';
import { QueueRequestInterceptor } from './interceptors/queue-request-interceptor';

import { AccreditationSearchQuery } from './models/educational-provider/accreditation-search-query.interface';
import { AppCurrencyConversion } from './models/application/app-currency-conversion.interface';
import { Application } from './models/application/application.interface';
import { ApplicationStatus } from './models/application/application-status.interface';
import { ApplicationSubmission } from './models/application/application-submission.interface';
import { ApplicationComments } from './models/application/application-comments.interface';
import { ApplicationDetail } from './models/application/application-detail.interface';
import { ApplicationDocuments } from './models/application/application-documents.interface';
import { ApplicationDocumentMetadata } from './models/application/application-document-metadata.interface';
import { ApplicationDocumentType } from './models/application/application-document-type.interface';
import { ApplicationSearchConfig } from './models/application/application-search.interface';
import { Grade } from './models/application/grade.interface';
import { ApplicationExpense } from './models/application/application-expense.interface';
import { ApplicationLearningType } from './models/application/application-learning-type.interface';
import { ApplicationProgramSubtype } from './models/application/application-program-subtype.interface';
import { BookEligibility } from './models/application/book-eligibility.interface';
import { BenefitPeriod } from './models/application/benefit-period.interface';
import { Course } from './models/application/course.interface';
import { Expense } from './models/application/expense.interface';
import {
	ClientEducationalProvider,
	EducationalProvider
} from './models/educational-provider/educational-provider.interface';
import { EducationProfile } from './models/application/education-profile.interface';
import { EducationalProviderSearchResult } from './models/educational-provider/educational-provider-search-result.interface';
import { EducationalProviderSearchQuery } from './models/educational-provider/educational-provider-search-query.interface';
import { EligibilityEventHistory } from './models/application/eligibility-event-history.interface';
import { ExtendedEligibilityEventHistory } from './models/application/extended-eligibility-event-history.interface';
import { FastTrackProviderSearchQuery } from './models/fast-track/fast-track-provider-search-query.interface';
import { FinancialAidSource } from './models/application/financial-aid-source.interface';
import { FlatApplication } from './models/application/flat-application.interface';
import { MicroApplication } from './models/application/micro-application.interface';
import { Participant, ParticipantResults } from './models/participant/participant.interface';
import { ParticipantSearchResult } from './models/participant/participant-search-result.interface';
import { ParticipantSearchQuery } from './models/participant/participant-search-query.interface';
import { ThinParticipant } from './models/participant/thin-participant.interface';
import {
	Supervisor,
	SupervisorResponse,
	SupervisorTable,
	ParticipantSupervisor
} from './models/participant/supervisor.interface';
import { Payment } from './models/payment/payment.interface';
import { PaymentDetails } from './models/payment/payment-details.interface';
import { PaymentBatchStatus } from './models/payment/payment-batch-status.interface';
import { PaymentEarningsCode } from './models/payment/payment-earnings-code.interface';
import { PaymentBatchReviewAction } from './models/payment/payment-batch-review-action.interface';
import { PaymentBatchCreateBatchScheduleInstance } from './models/payment/payment-batch-create-batch-schedule-instance.interface';
import { CreateBatchScheduleInstance } from './models/payment/create-batch-schedule-instance.interface';
import { PaymentBatchReRun } from './models/payment/payment-batch-rerun.interface';
import { PaymentBatchCreateBatch } from './models/payment/payment-batch-create-batch.interface';
import { PaymentBatchConfiguration } from './models/payment/payment-batch-configuration.interface';
import { PaymentBatchProcessFilterConfig } from './models/payment/payment-batch-process-filter-config.interface';
import { Refund } from './models/payment/refund.interface';
import { ContentItem } from './models/content/content-item.interface';
import { ContentOptions } from './models/content/content-options.interface';
import { CapInfoOptions } from './models/cap-info/cap-info-options.interface';
import { CapInfo } from './models/cap-info/cap-info.interface';
import { CapInfoSelections } from './models/cap-info/cap-info-selections.interface';
import { CapInfoResponse } from './models/cap-info/cap-info-response.interface';
import { ClientLoginDetails } from './models/login/client-login-details.interface';
import { ConsolidatedLoginBenefits } from './models/login/consolidated-login-benefits.interface';
import { LoginContentResponse } from './models/login/login-content-response.interface';
import { LoginRequest } from './models/login/login-request.interface';
import { ApplicationStatusChangeRequest } from './models/application/application-status-change-request.interface';
import { ApplicationSaveCommentRequest } from './models/application/application-save-comment-request.interface';
import { User } from './models/user/user.interface';
import { MessageSource } from './models/application/message-source.interface';
import { PaginationResult } from './models/pagination/pagination-result.interface';
import { PaginationQuery } from './models/pagination/pagination-query.interface';
import { ProviderAccreditation } from './models/provider/provider-accreditation.interface';
import { RuleException } from './models/application/rule-exception.interface';
import { EventLog } from './models/event-log/event-log.interface';
import { EventLogPatch } from './models/event-log/event-log-patch.interface';
import { EventLogSubject } from './models/event-log-subject/event-log-subject.interface';
import { WorkflowResults } from './models/workflow/workflow-results.interface';
import { ApplicationTotals } from './models/application/application-totals.interface';
import { RulesOutput } from './models/rules/rules-output.interface';
import { DegreeObjective } from './models/application/degree-objective.interface';
import { CurrentEnrollmentUnivProgram } from './models/application/current-enrollment-univ-program';
import { HighestLevelOfEducation } from './models/application/highest-level-of-education.interface';
import { AvailableCoachingSlot } from './models/advising/available-coaching-slot.interface';
import {
	ClientCourseOfStudy,
	CourseOfStudy,
	ProgramCourseOfStudy
} from './models/application/course-of-study.interface';
import { ProjectedParticipantProgramCompletion } from './models/application/projected-participant-program-completion.interface';
import { EducationalProviderDetail } from './models/educational-provider-detail.interface';
import { Client } from './models/application/client.interface';
import { System } from './models/system.interface';
import { SystemEventLog } from './models/system-event-log.interface';
import { SystemEventLogCategory } from './models/system-event-log-category.interface';
import { SystemEventLogSubject } from './models/system-event-log-subject.interface';
import { ApplicationSessionInfo } from './models/application/session-info.interface';
import { EducationPathway } from './models/education-pathway/education-pathway.interface';
import { CareerPathway } from './models/career-pathway/career-pathway.interface';
import { Workflow } from './models/workflow/workflow.interface';
import { AccreditingBody } from './models/application/accrediting-body.interface';
import { State } from './models/application/state.interface';
import { Address } from './models/participant/address.interface';
import { ProxyReviewReason } from './models/participant/proxy-review-reason.interface';
import { ProxyReviewer } from './models/participant/proxy-reviewer.interface';
import { CertificationFeature } from './models/program/certification-feature.interface';
import { DependentFeature } from './models/program/dependent-feature.interface';
import { DocumentFeature } from './models/program/document-feature.interface';
import { EducationGoal } from './models/education-goal/education-goal.interface';
import { ExpenseType } from './models/application/expense-type.interface';
import { ExpensePermissionType } from './models/application/expense-permission-type.interface';
import { ExpensesRefresh } from './models/expenses/expenses-refresh.interface';
import { OrphanedDocumentResults } from './models/orphaned-documents/orphaned-document-results.interface';
import { OrphanedDocument } from './models/orphaned-documents/orphaned-document.interface';
import { OrphanedDocumentIndex } from './models/orphaned-documents/orphaned-document-index.interface';
import { DocumentTag } from './models/documents/document-tag.interface';
import { DocumentSource } from './models/documents/document-source.interface';
import { OrphanedDocumentFilterQuery } from './models/orphaned-documents/orphaned-document-filter-query.interface';
import { ApplicationSearchData } from './models/search/application-search-data.interface';
import { ApplicationDocumentsOcr } from './models/application/application-documents-ocr.interface';
import { ApplicationDocumentOcrDataIssue } from './models/application/application-document-ocr-data-issue.interface';
import { OcrValidatedCourseExtract } from './models/application/application-document-ocr-validated-course-extract.interface';
import { OcrValidatedStudentExtract } from './models/application/application-document-ocr-validated-student-extract.interface';
import { OcrValidatedExpenseExtract } from './models/application/application-document-ocr-validated-expense-extract.interface';
import { OcrValidatedDirectBillExtract } from './models/application/application-document-ocr-validated-direct-bill-extract.interface';
import { OcrValidatedReimbursementExtract } from './models/application/application-document-ocr-validated-reimbursement-extract.interface';
import { DocumentDefaultFeature } from './models/program/document-default-feature.interface';
import { WellsFargoPaymentInfo } from './models/payment-portal/wells-fargo-payment-info.interface';
import { LiveChat } from './models/live-chat/live-chat.interface';
import { Product } from './models/product/product.interface';
import { ParticipantApplicationResult } from './models/participant/participant-application-result.interface';
import { BenefitPeriodFilterOptions } from './models/benefit-period/benefit-period-filter-options.interface';
import { ApplicationPendingReviewQueue } from './models/application/application-pending-review-queue.interface';
import { ApplicationType } from './models/application/application-type.interface';
import { ApplicationBulkReviewType } from './models/application/application-bulk-review-type.interface';
import { AdvisingCategory } from './models/advising/advising-category.interface';
import { AdvisingAppointment } from './models/advising/advising-appointment.interface';
import { MicroApps } from './models/micro-apps/micro-apps.interface';

import { Client as MicroClient } from './models/client/client.interface';
import { VirtualCreditCardOptionsInterface } from './models/client/virtual-credit-card-options.interface';
import { AddressType as ClientAddressType } from './models/client/address-type.interface';
import { AddressType as ParticipantAddressType } from './models/participant/address-type.interface';
import { Comment } from './models/comments/comment.interface';
import { UsernameType } from './models/client/username-type.interface';
import {
	ClientFeatureApprovalDelegation,
	ClientFeatureCareerPathways,
	ClientFeatureEducationNetwork,
	ClientFeatureLiveChat,
	ClientFeaturePhoneSupport,
	ClientFeatureMilestones,
	ClientFeaturePathways,
	ClientFeaturePaymentOptions,
	ClientFeatureRefDTO,
	ClientFeatureRepayment,
	ClientFeatures,
	ClientFeatureSelfScheduleCoaching,
	ClientFeatureSteps,
	ClientFeatureStudentLoanSupport,
	ClientFeatureTaxOptions,
	ClientFeatureVirtualCreditCard,
	ClientFeatureStudentGraduation
} from './models/client/client-features.interface';
import { LoanProductType } from './models/client/loan-product-type.interface';
import { ParticipantExceptionRuleHistoryPage } from './models/participant/participant-exception-rule-history-page.interface';
import { ParticipantExceptionRuleHistory } from './models/participant/participant-exception-rule-history.interface';
import { TaxSummary, TaxSummaryQueryParams } from './models/program/tax-summary.interface';
import { Savings, SavingsSubCategory } from './models/savings/savings.interface';

import { SearchQuery } from './models/search/search-query.interface';
import { SearchResult } from './models/search/search-result.interface';
import { UserResults } from './models/user/user-results.interface';
import { ApplicationService } from './services/application/application.service';
import { ApplicationHistoryService } from './services/application-history/application-history.service';
import { APPLICATION_HISTORY_FILTER_TYPE } from './enums/application-history-filter-type';
import { ApplicationHistoryFilterItem } from './models/application/application-history-filter-item.interface';
import { ApplicationDocumentsService } from './services/application-documents/application-documents.service';
import { ApplicationGradesService } from './services/application-grades/application-grades.service';
import { AuthenticationService } from './services/auth/auth.service';
import { HybridAuthService } from './services/hybrid-auth/hyrbid-auth.service';
import { ContentService } from './services/content/content.service';
import { CapInfoService } from './services/cap-info/cap-info.service';
import { WorkflowService } from './services/workflow/workflow.service';
import { UserService } from './services/user/user.service';
import { UserConfigService } from './services/user/config/user-config.service';
import { ParticipantService } from './services/participant/participant.service';
import { DegreeObjectiveService } from './services/degree-objective/degree-objective.service';
import { CourseOfStudyService } from './services/course-of-study/course-of-study.service';
import { EducationProviderService } from './services/education-provider/education-provider.service';
import { StateService } from './services/state/state.service';
import { ProgramService } from './services/program/program.service';
import { ClientService } from './services/client/client.service';
import { ClientAdminService } from './services/application/client-admin.service';
import { PermissionService } from './services/permission/permission.service';
import { SearchService } from './services/search/search.service';
import { FastTrackService } from './services/fast-track/fast-track.service';
import { FinancialAidSourceService } from './services/grants/financial-aid-source.service';
import { PaymentPortalService } from './services/payment-portal/payment-portal.service';
import { AppealService } from './services/appeal/appeal.service';
import { RulesService } from './services/rules/rules.service';
import { SupportTicketService } from './services/support-ticket/support-ticket.service';

import { FileService } from './services/file/file.service';
import { EducationPathwayService } from './services/education-pathways/education-pathway.service';
import { EducationPathwayMilestone } from './models/education-pathway/education-pathway-milestone.interface';
import { EducationPathwayMilestoneIcon } from './models/education-pathway/education-pathway-milestone-icon-response.interface';
import { EducationPathwayStep } from './models/education-pathway/education-pathway-step.interface';
import { MilestoneStepType } from './models/education-pathway/milestone-step-type.interface';
import { LogoType } from './models/education-pathway/logo-type.interface';
import { Program } from './models/application/program.interface';
import { ProgramType } from './models/application/program-type.interface';
import { ProviderDocument } from './models/content/provider-document.interface';
import { Notification } from './models/notifications/notification.interface';
import { Email } from './models/notifications/email.interface';
import { NOTIFICATION_EVENT_TYPE } from './enums/notification-event-type';
import { ProductType } from './models/application/product-type.interface';

import { PaymentService } from './services/payment/payment.service';
import { PaymentHistory } from './models/payment/payment-history.interface';
import { PaymentBatch } from './models/payment/payment-batch.interface';
import { PaymentBatchDetails } from './models/payment/payment-batch-details.interface';
import { PaymentBatchDefinition } from './models/payment/payment-batch-definition.interface';
import { PaymentBatchDefinitionUpdate } from './models/payment/payment-batch-definition-update.interface';
import { PaymentBatchSchedule } from './models/payment/payment-batch-schedule.interface';
import { PaymentBatchScheduleInstance } from './models/payment/payment-batch-schedule-instance.interface';
import { PaymentBatchLog } from './models/payment/payment-batch-log.interface';
import { PaymentBatchLogResponse } from './models/payment/payment-batch-log-response.interface';
import { PaymentBatchStatusAction } from './models/payment/payment-batch-status-action.interface';
import { PaymentBatchFileDelivery } from './models/payment/payment-batch-file-delivery.interface';
import { PaymentBatchConfigurationAudit } from './models/payment/payment-batch-configuration-audit.interface';
import { PaymentBatchConfigurationAuditAction } from './models/payment/payment-batch-configuration-audit-action.interface';
import { ApplicationSearchQuery } from './models/application/application-search-query.interface';
import { ApplicationSearchResult } from './models/application/application-search-result.interface';
import { FilterOptions } from './models/filters/filter-options.interface';
import { ParticipantExpenseHistoryFilters } from './models/participant/participant-expense-history-filter.interface';
import { ParticipantExpenseHistoryQuery } from './models/participant/participant-expense-history-query.interface';
import { ProviderDocumentsService } from './services/provider-documents/provider-documents.service';
import { OrphanedDocumentsService } from './services/orphaned-documents/orphaned-documents.service';
import { DocumentsService } from './services/documents/documents-service';
import { NotificationService } from './services/notifications/notification.service';
import { LiveChatService } from './services/live-chat/live-chat.service';
import { ActiveProductService } from './services/product/active-product.services';
import { CareerPathwayService } from './services/career-pathways/career-pathway.service';
import { AdvisingService } from './services/advising/advising.service';
import { ProgramAccreditingBody } from './models/program/program-accrediting-body.interface';
import { ImpersonationService } from './services/impersonation/impersonation.service';
import { PaymentFileColumnData } from './models/payment/payment-file-column-data.interface';
import { PaymentFileFooterField } from './models/payment/payment-file-footer-field.interface';
import { PaymentFileHeaderField } from './models/payment/payment-file-header-field.interface';
import { PaymentFileFields } from './models/payment/payment-file-fields.interface';
import { PaymentConfigField } from './models/payment/payment-config-field.interface';
import { BatchDefinitionClientNotification } from './models/payment/batch-definition-client-notification.interface';
import { RetirementEnrollmentHistory } from './models/loan/retirement-enrollment-history.interface';

// PATCH Objects
import { ApplicationDocumentsPatch } from './models/patch/application-documents.patch.interface';
import { ApplicationCommentsPatch } from './models/patch/application-comments.patch.interface';
import { OrphanedDocumentPatch } from './models/patch/orphaned-document.patch.interface';
import { ApplicationDetailPatch } from './models/patch/application-detail.patch.interface';

// HTTP Utilities:
import { HttpParamObject } from './utility/http-utility/http-param-object.interface';
import { HttpUtilityService } from './utility/http-utility/http-utility.service';
import { HttpPatchUtilityService } from './utility/http-patch-utility/http-patch-utility.service';

import { NgModule } from '@angular/core';
import { MilestoneStepProgram } from './models/education-pathway/milestone-step-program.interface';
import { DefaultDocuments } from './models/documents/default-documents.interface';
import { OcrValidatedBookExtract } from './models/application/application-document-ocr-validated-book-extract.interface';
import { AutomatedDocumentConfigurationTableData } from './models/application/automated-document-configuration-table-data.interface';
import { AutomatedDocumentConfigurationDTO } from './models/application/automated-document-configuration-dto.interface';
import { AdvisingSession } from './models/participant/advising-session.interface';
import { ParticipantRepaymentInformation } from './models/participant/participant-repayment-information';
import { ParticipantRepaymentSetup } from './models/participant/participant-repayment-setup';
import { NotificationResult } from './models/notifications/notification-result.interface';
import { CourseService } from './services/course/course.service';
import { CourseMethod } from './models/application/course-method.interface';
import { EducationalAccessProgramProvider } from './models/program/educational-access-program-provider.interface';
import { CAP_INFO_TYPE } from './enums/cap-info-type';
import { WebinarService } from './services/webinar/webinar.service';
import { Webinar } from './models/webinar/webinar.interface';
import { ApplicationPaymentVerification } from './models/application/application-payment-verification.interface';
import { ResourcesForLearnersService } from './services/webinar/resources-for-learners.service';
import { AdvisingAvailableDates } from './models/advising/advising-available-dates.interface';
import { Province } from './models/participant/province.interface';
import { ParticipantDetail } from './models/participant/participant-detail.interface';
import { ParticipantException } from './models/participant/participant-exception.interface';
import { ParticipantExceptionPatch } from './models/participant/participant-exception-patch.interface';
import { Country } from './models/participant/country.interface';
import { ProgramFeatureTableData } from './models/program/program-feature-table-data.interface';
import { ProgramFeatureReference } from './models/program/program-feature-reference.interface';
import { MilestoneIcons } from './models/client/milestone-icons.interface';
import { NotificationAccess } from './models/notifications/notification-access.interface';
import { AnnouncementStatus } from './models/notifications/announcement-status.interface';
import { WorkType } from './models/workflow/worktype.interface';
import { StepProgram } from './models/client/step-program.interface';
import { StepType } from './models/client/step-type.interface';
import { WorkflowAssignee } from './models/workflow/workflow-assignee.interface';
import { SupervisorDefaultView } from './models/workflow/supervisor-default-view.interface';
import { ApplicationSource } from './models/application/application-source.interface';
import { ApplicationSourceService } from './services/application-source/application-source.service';
import { ThinProgram } from './models/client/thin-program.interface';
import { DataFilter } from './models/workflow/data-filter.interface';
import { WorkflowListTableData } from './models/workflow/workflow-list-table-data.interface';
import { WorkflowAssignmentMapping } from './models/workflow/workflow-queue-assignment-mapping.interface';
import { WorkflowListTableTabView } from '@internal-admin/app/modules/workflow/workflow-list-table-data.interface';
import { WorkflowAssignmentMappingRequest } from './models/workflow/workflow-queue-assignment-mapping-request.interface';
import { WorkflowAssignmentMappingResults } from './models/workflow/workflow-queue-assignment-mapping-result.interface';
import { ProgramFeatures } from './models/program/program-feature.interface';
import { Dependent } from './models/dependent/dependent.interface';
import { DependentType } from './models/dependentType/dependent-type.interface';
import { WorkflowAssignmentMappingReassignRequest } from './models/workflow/workflow-assignment-mapping-reassign-request.interface';
import { PaymentBatchService } from './services/payment/payment-batch.service';
import { PaymentConfigService } from './services/payment/payment-config.service';
import { PaymentFileService } from './services/payment/payment-file.service';
import { VirtualCardExpirationOptionType } from './models/program/virtual-card-expiration-option-type.interface';
import { UserType } from './models/user/user-type.interface';
import { WorkflowUser } from './models/workflow/workflow-user.interface';
import { Taxability } from './models/program/taxability.interface';
import { PriorEducationDegree } from './models/program/prior-education-degree.interface';
import { ProviderCategoryType } from './models/program/provider-category-type.interface';
import { EducationAccessProgramFeature } from './models/program/education-access-program-feature.interface';
import { FastTrackProgramFeature } from './models/program/fast-track-program-feature.interface';
import { PriorEducationDegreeProgram } from './models/program/prior-education-degree-program.interface';
import {
	WorkflowSupervision,
	WorkflowSupervisionUserTableData,
	WorkflowSupervisorAssignee
} from './models/workflow/workflow-supervision.interface';
import { WorkflowTimeOffTableData } from './models/workflow/workflow-time-off.interface';
import { WorkflowProcessorTimeOff } from './models/workflow/workflow-processor-time-off.interface';
import { WorkflowProcessorTimeOffRequest } from './models/workflow/workflow-processor-time-off-request.interface';
import { WorkflowProcessorTimeOffResults } from './models/workflow/workflow-processor-time-off-result.interface';
import { CapLimitCalculation } from './models/program/cap-limit-calculation.interface';
import { DeterminationType } from './models/program/determination-type.interface';
import { ProgramExpenseType } from './models/program/program-expense-type.interface';
import {
	WorkflowTaskOverview,
	WorkflowTaskOverviewResult,
	WorkflowTaskOverviewData,
	Slas,
	ClientIds
} from './models/workflow/supervisor-summary.interface';
import { ProgramGrade } from './models/program/program-grade.interface';
import { ProgramDegreeType } from './models/application/program-degree-type.interface';
import { SSOConfigType } from './models/client/sso-config-type.interface';
import { CourseOfStudyCategory } from './models/application/course-of-study-category.interface';
import { ProgramLoanFrequency } from './models/program/program-loan-frequency.interface';
import { ProgramLoanFeature } from './models/program/program-loan-feature.interface';
import { ApplicationCurrency } from './models/currency/application-currency.interface';
import { Currency } from './models/currency/currency.interface';
import { CurrencyService } from './services/currency/currency.service';
import { NonDegreeFieldOfStudy } from './models/program/non-degree-field-of-study.interface';
import { ProgramLearningType } from './models/program/program-learning-type.interface';
import { ProgramStudentLoanServicerType } from './models/program/program-student-loan-servicer-type.interface';
import { StudentLoanServicerType } from './models/loan/student-loan-servicer-type.interface';
import { VirtualCardDetailsService } from './services/virtual-card-details/virtual-card-details.service';
import {
	VirtualCardDetails,
	VirtualCardTransactions
} from './models/virtual-card-details/vitual-card-details.interface';
import { ApplicationComment } from './models/program/application-comment.interface';
import { ConditionalFieldsFeature } from './models/program/condtional-fields-feature.interface';
import { ProgramCCL } from './models/program/program-ccl.interface';
import { AppealFeature } from './models/program/appeal-feature.interface';
import { AppealStatusChangeRequest } from './models/appeal/appeal-status-change-request.interface';
import { ProgramAppealReason } from './models/program/program-appeal-reason.interface';
import { AppealApproverLevel } from './models/program/appeal-approver-level.interface';
import { AppealReason } from './models/program/appeal-reason.interface';
import { BulkApprovers } from './models/program/bulk-approvers.interface';
import { BenefitPeriodType } from './models/benefit-period/benefit-period-type.interface';
import { BenefitPeriodLimit } from './models/benefit-period/benefit-period-limit.interface';
import { ClientPayrollDate } from './models/client/client-payroll-date.interface';
import { ExceptionRuleService } from './services/exception-rules/exception-rule.service';
import { ExceptionRule } from './models/exception-rule/exception-rule.interface';
import { ExceptionRuleSearchResult } from './models/exception-rule/exception-rule-search-result.interface';
import { ProgramAppealConfig } from './models/application/program-appeal-configuration.interface';
import { LoanService } from './services/loan/loan.service';
import { LoanPayment } from './models/loan/loan-payment.interface';
import { LoanPaymentDetail } from './models/loan/loan-payment-detail.interface';
import { LoanRepaymentStatus } from './models/loan/loan-repayment-status.interface';
import { LoanAccount } from './models/loan/loan-account.interface';
import { LoanServicer } from './models/loan/loan-servicer.interface';
import { LoanServicerDetail } from './models/loan/loan-servicer-detail.interface';
import { LoanApplication } from './models/loan/loan-application.interface';
import { StudentLoanType } from './models/loan/student-loan-type.interface';
import { ProviderCourseService } from './services/provider-course/provider-course.service';
import {
	DataShareProvider,
	DataShareProviderProgramType,
	DataShareExplorationSurvey,
	DataShareMarketPlaceSurvey,
	DataShareExplorationSurveyTable
} from './models/data-share-provider/data-share-provider.interface';
import { StudentLoanPaymentHistory } from './models/loan/student-loan-payment-history.interface';
import { ParticipantTicketsHistory } from './models/participant/tickets-history.interface';
import { Rule } from './models/rules/rules.interface';
import { FlatLoanApplication } from './models/loan/flat-loan-application.interface';
import { LoanHistoryFilters } from './models/participant/loan-history-filters.interface';
import { LoanHistoryQuery } from './models/participant/loan-history-query.interface';
import { AppealHistory, AppealStatus, Appeal } from './models/appeal/appeal.interface';
import { LoanFrequency } from './models/loan/loan-frequency.interface';
import { RefundReason } from './models/payment/refund-reason.interface';
import { ReturnedReason } from './models/payment/returned-reason.interface';
import { RepaymentOwner } from './models/payment/repayment-owner.interface';
import { DocumentUpload } from './models/documents/document-upload.interface';
import { RefundService } from './services/refund/refund.service';
import { RefundMethod } from './models/payment/refund-method.interface';
import { RefundCorrectionReason } from './models/payment/refund-correction-reason.interface';
import { LoanNavigationService } from './services/loan-navigation/loan-navigation.service';
import { RefundDistribution } from './models/payment/refund-distribution.interface';
import { CombinedExpense } from './models/application/combined-expense.interface';
import { RefundHistory } from './models/payment/refund-history-interface';
import { EducationalProviderAdminSearch } from './models/educational-provider/educational-provider-admin-search.interface';
import { LoanStepHeaderService } from './services/loan-step-headers/loan-step-header.service';
import { ParticipantAccountSnapshot } from './models/participant/participant-account-snapshot.interface';
import { ProgramProvider } from './models/educational-provider/program-provider.interface';
import { SupportTicket } from './models/support-ticket/support-ticket.interface';
import {
	SupportTicketAttachment,
	SupportTicketComment
} from './models/support-ticket/support-ticket-comment.interface';
import { SupportTicketDocument } from './models/support-ticket/support-ticket-document.interface';
import { DataShareEducationalProvider } from './models/data-share-provider/data-share-educational-provider.interface';
import { ProviderClient } from './models/provider/provider-client.interface';
import {
	ProviderCourseOfStudy,
	ProviderCourseOfStudyResponse
} from './models/provider/provider-course-of-study.interface';
import { ProviderSubscription } from './models/provider/provider-subscription.interface';
import { EducationalProviderCode } from './models/provider/educational-provider-code.interface';
import { LoanServicerService } from './services/loan/loan-servicer.service';
import { ProgramProvidersSearch } from './models/educational-provider/program-providers-search.interface';
import { ApplicationWorkflow } from './services/workflow/application-workflow.interface';
import { Agreement } from './models/agreements/agreement.interface';
import { AgreementsData } from './models/agreements/agreements-data.interface';
import { LoanApplicationService } from './services/loan/loan-application.service';
import { LoanApplicationSubmissionResponse } from './models/loan/loan-application-submission-response.interface';
import { CreateParticipantForm } from './models/participant/create-participant-form';
import { CommentsService } from './services/comments/comments.service';

@NgModule({})
class EdAssistAPIModule {}

export {
	Appeal,
	EdAssistAPIModule,
	ApiEndpointInterceptor,
	ErrorInterceptor,
	HttpCredentialsInterceptor,
	HttpHeadersInterceptor,
	QueueRequestInterceptor,
	AuthGuard,
	HybridAuthGuard,
	LoginGuard,
	PermissionRouteGuard,
	ProjectXGuard,
	FileService,
	AccreditationSearchQuery,
	AppCurrencyConversion,
	Application,
	ApplicationStatus,
	ApplicationSubmission,
	ApplicationComments,
	Comment,
	ApplicationDetail,
	ApplicationDocuments,
	ApplicationDocumentType,
	ApplicationSearchConfig,
	ApplicationDocumentMetadata,
	BenefitPeriodFilterOptions,
	BookEligibility,
	Grade,
	ApplicationExpense,
	ApplicationLearningType,
	ApplicationProgramSubtype,
	BenefitPeriod,
	LoanPayment,
	LoanPaymentDetail,
	LoanRepaymentStatus,
	LoanAccount,
	LoanServicer,
	LoanServicerDetail,
	LoanApplicationSubmissionResponse,
	LoanApplication,
	Course,
	CommentsService,
	CombinedExpense,
	RefundDistribution,
	CourseMethod,
	DefaultDocuments,
	Expense,
	EducationalProvider,
	EducationalProviderAdminSearch,
	ClientEducationalProvider,
	EducationalProviderSearchResult,
	EducationalProviderSearchQuery,
	EducationProfile,
	EducationGoal,
	EducationalAccessProgramProvider,
	EligibilityEventHistory,
	ExtendedEligibilityEventHistory,
	ExpenseType,
	ExpensePermissionType,
	ExpensesRefresh,
	FastTrackProviderSearchQuery,
	FinancialAidSource,
	FlatApplication,
	MicroApplication,
	PaginationResult,
	PaginationQuery,
	Participant,
	ParticipantResults,
	ParticipantExceptionRuleHistoryPage,
	ParticipantExceptionRuleHistory,
	ParticipantSearchResult,
	ParticipantSearchQuery,
	ProviderDocument,
	Supervisor,
	SupervisorResponse,
	SupervisorTable,
	ParticipantSupervisor,
	Payment,
	PaymentDetails,
	PaymentBatchStatus,
	PaymentBatchReviewAction,
	PaymentBatchCreateBatchScheduleInstance,
	CreateBatchScheduleInstance,
	PaymentBatchReRun,
	PaymentBatchCreateBatch,
	Program,
	AutomatedDocumentConfigurationTableData,
	ProgramFeatureTableData,
	AutomatedDocumentConfigurationDTO,
	ProgramType,
	Refund,
	RefundReason,
	ReturnedReason,
	RefundCorrectionReason,
	RepaymentOwner,
	DocumentUpload,
	RefundMethod,
	ContentItem,
	ContentOptions,
	CapInfoOptions,
	CapInfo,
	CapInfoSelections,
	CapInfoResponse,
	CAP_INFO_TYPE,
	ClientLoginDetails,
	ConsolidatedLoginBenefits,
	Client,
	SSOConfigType,
	VirtualCreditCardOptionsInterface,
	ThinProgram,
	Taxability,
	ProgramGrade,
	ClientFeatureVirtualCreditCard,
	System,
	LoginContentResponse,
	LoginRequest,
	ApplicationStatusChangeRequest,
	ApplicationSaveCommentRequest,
	User,
	UserType,
	WorkflowUser,
	MessageSource,
	ProviderAccreditation,
	ProviderCourseOfStudy,
	ProviderCourseOfStudyResponse,
	ProviderClient,
	ProviderSubscription,
	RuleException,
	EventLog,
	EventLogPatch,
	EventLogSubject,
	WorkflowResults,
	DataFilter,
	ApplicationTotals,
	RulesOutput,
	DegreeObjective,
	ProgramDegreeType,
	HighestLevelOfEducation,
	CurrentEnrollmentUnivProgram,
	AvailableCoachingSlot,
	AdvisingAvailableDates,
	CourseOfStudy,
	ProgramCourseOfStudy,
	CourseOfStudyCategory,
	ProjectedParticipantProgramCompletion,
	ThinParticipant,
	EducationalProviderDetail,
	ApplicationSessionInfo,
	EducationPathway,
	EducationPathwayMilestone,
	EducationPathwayMilestoneIcon,
	EducationPathwayStep,
	MilestoneStepProgram,
	MilestoneStepType,
	CareerPathway,
	LogoType,
	Workflow,
	AccreditingBody,
	State,
	PaymentHistory,
	PaymentBatch,
	PaymentBatchDetails,
	PaymentBatchDefinition,
	PaymentBatchDefinitionUpdate,
	PaymentFileColumnData,
	PaymentFileFooterField,
	PaymentFileHeaderField,
	PaymentFileFields,
	PaymentConfigField,
	BatchDefinitionClientNotification,
	PaymentBatchSchedule,
	PaymentBatchScheduleInstance,
	PaymentBatchLog,
	PaymentBatchLogResponse,
	PaymentBatchStatusAction,
	PaymentBatchFileDelivery,
	PaymentBatchConfiguration,
	PaymentBatchProcessFilterConfig,
	PaymentBatchConfigurationAudit,
	PaymentBatchConfigurationAuditAction,
	PaymentEarningsCode,
	ApplicationSearchResult,
	ApplicationSearchQuery,
	Address,
	Province,
	Country,
	ProxyReviewReason,
	ProxyReviewer,
	FilterOptions,
	ParticipantExpenseHistoryFilters,
	ParticipantExpenseHistoryQuery,
	CertificationFeature,
	DependentFeature,
	DocumentFeature,
	OrphanedDocumentResults,
	OrphanedDocument,
	OrphanedDocumentIndex,
	OrphanedDocumentFilterQuery,
	DocumentTag,
	DocumentSource,
	SearchQuery,
	ApplicationSearchData,
	SearchResult,
	UserResults,
	ApplicationDocumentsOcr,
	ApplicationDocumentOcrDataIssue,
	OcrValidatedCourseExtract,
	OcrValidatedBookExtract,
	OcrValidatedStudentExtract,
	OcrValidatedExpenseExtract,
	OcrValidatedDirectBillExtract,
	OcrValidatedReimbursementExtract,
	DocumentDefaultFeature,
	WellsFargoPaymentInfo,
	Notification,
	Email,
	NotificationResult,
	NotificationAccess,
	AnnouncementStatus,
	ProductType,
	LiveChat,
	Product,
	ParticipantApplicationResult,
	AdvisingSession,
	AdvisingAppointment,
	ParticipantRepaymentInformation,
	ParticipantRepaymentSetup,
	ApplicationPendingReviewQueue,
	ApplicationType,
	ApplicationBulkReviewType,
	AdvisingCategory,
	SavingsSubCategory,
	Savings,
	ParticipantAddressType,
	Webinar,
	ParticipantDetail,
	ParticipantException,
	ParticipantExceptionPatch,
	ProgramFeatureReference,
	ProgramFeatures,
	Dependent,
	DependentType,
	VirtualCardExpirationOptionType,
	PriorEducationDegree,
	ProviderCategoryType,
	EducationAccessProgramFeature,
	FastTrackProgramFeature,
	PriorEducationDegreeProgram,
	WorkflowAssignee,
	WorkflowAssignmentMapping,
	WorkflowListTableData,
	WorkflowListTableTabView,
	WorkflowAssignmentMappingRequest,
	WorkflowAssignmentMappingResults,
	MicroApps,
	WorkflowAssignmentMappingReassignRequest,
	WorkflowSupervisionUserTableData,
	WorkflowSupervision,
	WorkflowSupervisorAssignee,
	WorkflowTimeOffTableData,
	WorkflowProcessorTimeOff,
	WorkflowProcessorTimeOffRequest,
	WorkflowProcessorTimeOffResults,
	WorkflowTaskOverview,
	WorkflowTaskOverviewResult,
	WorkflowTaskOverviewData,
	Slas,
	ClientIds,
	ApplicationCurrency,
	Currency,
	VirtualCardDetails,
	VirtualCardTransactions,
	MicroClient,
	ClientAddressType,
	UsernameType,
	ClientFeatures,
	ClientFeatureRefDTO,
	ClientFeatureEducationNetwork,
	ClientFeatureApprovalDelegation,
	ClientFeatureSelfScheduleCoaching,
	ClientFeaturePaymentOptions,
	ClientFeatureStudentLoanSupport,
	ClientFeatureLiveChat,
	ClientFeaturePhoneSupport,
	ClientFeatureTaxOptions,
	ClientFeaturePathways,
	ClientFeatureMilestones,
	LoanProductType,
	ClientFeatureRepayment,
	ClientFeatureSteps,
	MilestoneIcons,
	StepProgram,
	StepType,
	ClientFeatureCareerPathways,
	ClientCourseOfStudy,
	ApplicationDocumentsPatch,
	ApplicationCommentsPatch,
	OrphanedDocumentPatch,
	ApplicationDetailPatch,
	ParticipantAccountSnapshot,
	ApplicationService,
	ApplicationDocumentsService,
	ApplicationGradesService,
	ApplicationHistoryService,
	APPLICATION_HISTORY_FILTER_TYPE,
	ApplicationHistoryFilterItem,
	AuthenticationService,
	HybridAuthService,
	ContentService,
	CapInfoService,
	WorkflowService,
	UserService,
	UserConfigService,
	ParticipantService,
	DegreeObjectiveService,
	CourseOfStudyService,
	ClientService,
	ClientAdminService,
	PermissionService,
	ProgramService,
	RefundService,
	LoanService,
	LoanApplicationService,
	LoanNavigationService,
	LoanServicerService,
	EducationProviderService,
	EducationPathwayService,
	StateService,
	PaymentService,
	PaymentBatchService,
	PaymentConfigService,
	PaymentFileService,
	ProviderDocumentsService,
	SearchService,
	OrphanedDocumentsService,
	DocumentsService,
	FastTrackService,
	FinancialAidSourceService,
	PaymentPortalService,
	NotificationService,
	LiveChatService,
	ActiveProductService,
	CourseService,
	CareerPathwayService,
	AdvisingService,
	WebinarService,
	ResourcesForLearnersService,
	CurrencyService,
	VirtualCardDetailsService,
	HttpParamObject,
	HttpUtilityService,
	HttpPatchUtilityService,
	NOTIFICATION_EVENT_TYPE,
	ApplicationPaymentVerification,
	WorkType,
	SupervisorDefaultView,
	ApplicationSource,
	ApplicationSourceService,
	CapLimitCalculation,
	DeterminationType,
	ProgramExpenseType,
	ProgramLoanFrequency,
	ProgramLoanFeature,
	NonDegreeFieldOfStudy,
	ProgramAccreditingBody,
	ProgramLearningType,
	ProgramStudentLoanServicerType,
	StudentLoanServicerType,
	ApplicationComment,
	ConditionalFieldsFeature,
	ProgramCCL,
	AppealFeature,
	ProgramAppealReason,
	AppealApproverLevel,
	AppealReason,
	BulkApprovers,
	BenefitPeriodType,
	BenefitPeriodLimit,
	ClientPayrollDate,
	ExceptionRuleService,
	ExceptionRule,
	ExceptionRuleSearchResult,
	ProgramAppealConfig,
	ProviderCourseService,
	DataShareProvider,
	DataShareProviderProgramType,
	DataShareExplorationSurvey,
	DataShareMarketPlaceSurvey,
	DataShareExplorationSurveyTable,
	DataShareEducationalProvider,
	ImpersonationService,
	StudentLoanType,
	StudentLoanPaymentHistory,
	ParticipantTicketsHistory,
	ParticipantAllHistoryTable,
	Rule,
	SystemEventLog,
	SystemEventLogCategory,
	SystemEventLogSubject,
	AppealService,
	RulesService,
	AppealHistory,
	FlatLoanApplication,
	Agreement,
	AgreementsData,
	LoanHistoryFilters,
	LoanHistoryQuery,
	AppealStatus,
	LoanFrequency,
	ClientFeatureStudentGraduation,
	AppealStatusChangeRequest,
	RefundHistory,
	LoanStepHeaderService,
	TaxSummary,
	TaxSummaryQueryParams,
	ProgramProvider,
	SupportTicketService,
	SupportTicket,
	SupportTicketComment,
	SupportTicketDocument,
	SupportTicketAttachment,
	EducationalProviderCode,
	ProgramProvidersSearch,
	ApplicationWorkflow,
	RetirementEnrollmentHistory,
	CreateParticipantForm
};
